/**
 * Admin credential handling for the Apprise VM form (pure logic).
 *
 * The password itself NEVER enters pipeline_config, the zustand store, browser storage,
 * URLs or the review text. "default_password" stores nothing (the server reads its own
 * configured secret); "custom_password" sends the write-only top-level `admin_password`.
 * Azure's password rules are mirrored here so problems show before submitting; the backend
 * re-verifies them.
 */
import type { AppriseCredentialMode, AppriseVmOptions } from '@/types'

export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 123

/** Obvious values Azure rejects (compared case-insensitively). */
const PASSWORD_DENY_LIST: ReadonlySet<string> = new Set([
  'abc@123', 'iloveyou!', 'p@$$w0rd', 'p@ssw0rd', 'p@ssword123', 'pa$$word', 'pass@word1', 'password!', 'password1', 'password22',
])

/** How many of lower / upper / digit / special the password contains (0–4). */
export function passwordClassCount(password: string): number {
  return [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length
}

export interface PasswordErrors {
  password?: string
  confirm?: string
}

/** Azure rules: 12–123 chars, 3 of 4 character classes, not containing the username, not an obvious value. */
export function validateAdminPassword(password: string, confirm: string, username: string): PasswordErrors {
  const out: PasswordErrors = {}
  if (!password) out.password = `Enter a password (${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters)`
  else if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH)
    out.password = `The password must be between ${PASSWORD_MIN_LENGTH} and ${PASSWORD_MAX_LENGTH} characters long`
  else if (passwordClassCount(password) < 3)
    out.password = 'Use at least 3 of: lowercase letter, uppercase letter, digit, special character'
  else if (username.trim() && password.toLowerCase().includes(username.trim().toLowerCase()))
    out.password = 'The password must not contain the user name'
  else if (PASSWORD_DENY_LIST.has(password.toLowerCase())) out.password = 'This password is too easy to guess — choose another'

  if (!confirm) out.confirm = 'Confirm the password'
  else if (confirm !== password) out.confirm = 'The passwords do not match'
  return out
}

export type PasswordStrengthLevel = 'none' | 'weak' | 'fair' | 'strong'

/** Simple strength hint (not a security guarantee; Azure's rules decide validity). */
export function passwordStrength(password: string): { level: PasswordStrengthLevel; label: string } {
  if (!password) return { level: 'none', label: '' }
  const classes = passwordClassCount(password)
  if (password.length < PASSWORD_MIN_LENGTH || classes < 3) return { level: 'weak', label: 'Weak' }
  if (password.length >= 16 && classes === 4) return { level: 'strong', label: 'Strong' }
  return { level: 'fair', label: 'Fair' }
}

export const isPasswordMode = (mode: AppriseCredentialMode): boolean => mode === 'default_password' || mode === 'custom_password'

export interface CredentialChoice {
  value: AppriseCredentialMode
  label: string
  /** Why the choice cannot be used right now (it is shown disabled). */
  disabledReason?: string
}

const GITHUB_PASSWORD_REASON = 'Supported only with the local executor — a password is never a workflow input'

/**
 * The credential options for the executor in use. The GitHub workflow cannot take a
 * password, so it offers Key Vault credentials instead; the default password is only
 * offered when the server has one configured.
 */
export function credentialChoices(
  options: Pick<AppriseVmOptions, 'default_password_available' | 'executor' | 'password_credentials_supported'>,
): CredentialChoice[] {
  const github = options.executor?.mode === 'github' || options.password_credentials_supported === false
  const choices: CredentialChoice[] = []
  if (github || options.default_password_available === true) {
    choices.push({
      value: 'default_password',
      label: 'Use the configured default password',
      disabledReason: github ? GITHUB_PASSWORD_REASON : undefined,
    })
  }
  choices.push({ value: 'custom_password', label: 'Custom password', disabledReason: github ? GITHUB_PASSWORD_REASON : undefined })
  if (github) {
    choices.push(
      { value: 'generate_in_key_vault', label: 'Generate and store in Azure Key Vault' },
      { value: 'existing_key_vault_secret', label: 'Use an existing Key Vault secret' },
    )
  }
  return choices
}

/**
 * Whether the requester picks how the admin credential is provided. Only where a password can be used (the local executor with
 * passwords switched on) is there anything to pick; everywhere else the credential is generated and stored in Key Vault.
 */
export function credentialPickerVisible(
  options: Pick<AppriseVmOptions, 'executor' | 'password_credentials_supported'>,
): boolean {
  return !(options.executor?.mode === 'github' || options.password_credentials_supported === false)
}

/** First usable choice (the configured default password whenever the server has one). */
export function defaultCredentialMode(
  options: Pick<AppriseVmOptions, 'default_password_available' | 'executor' | 'password_credentials_supported'>,
): AppriseCredentialMode {
  return credentialChoices(options).find((c) => !c.disabledReason)?.value ?? 'custom_password'
}

/**
 * Name of the Key Vault secret that holds (or will hold) the admin credential, '' when none is stored. Mirrors the Terraform
 * module: an existing secret keeps its name; otherwise <vm>-admin-password (Windows or a password) / <vm>-ssh-private-key (Linux key).
 */
export function storedSecretName(
  sec: { credential_mode: AppriseCredentialMode; key_vault_mode: string; credential_secret_name: string },
  serverName: string,
  os: string,
): string {
  if (sec.credential_mode === 'existing_key_vault_secret') return sec.credential_secret_name.trim()
  if (sec.key_vault_mode === 'none') return ''
  const vm = serverName.trim()
  return sec.credential_mode === 'generate_in_key_vault' && os === 'linux' ? `${vm}-ssh-private-key` : `${vm}-admin-password`
}

/** Review wording for the credential — never reveals anything about the value. */
export function credentialSummary(mode: AppriseCredentialMode, secretName = ''): string {
  switch (mode) {
    case 'default_password': return 'Default password (configured by your administrator)'
    case 'custom_password': return 'Custom password (hidden)'
    case 'generate_in_key_vault': return 'Generated by the pipeline and stored in Key Vault (never shown in the portal)'
    case 'existing_key_vault_secret': return `Existing Key Vault secret: ${secretName}`
  }
}
